import { Client, Room } from 'colyseus.js';
import {
    PlayerState,
    TileType,
    ChunkData,
    SlashData,
    PortalAddress,
    RodentType,
} from '@blockworld/shared';

// ============================================================
// NETWORK CLIENT
// Client-authoritative position sync
// ============================================================

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

// Position update sent to server
export interface PositionUpdate {
    x: number;
    y: number;
    state: PlayerState;
    facing: number;
    isCrouching: boolean;
    isHoldingBlock: boolean;
    heldBlockType: number;
    timestamp: number;
    sequence: number;
}

// Position snapshot received for other players
export interface PlayerSnapshot {
    id: string;
    x: number;
    y: number;
    state: PlayerState;
    facing: number;
    isCrouching: boolean;
    isHoldingBlock: boolean;
    heldBlockType: number;
    timestamp: number;
    sequence: number;
    // Combat
    hp: number;
    maxHp: number;
    isHoldingSword: boolean;
    // Equipment visual
    hasBoots: boolean;
}

export class NetworkClient {
    private client: Client;
    private room: Room | null = null;
    private serverUrl: string;
    private positionSequence = 0;
    private playerCount = 0; // Track player count locally

    // Callbacks
    onConnectionChange: ((status: ConnectionStatus) => void) | null = null;
    onPlayerCountChange: ((count: number) => void) | null = null;
    onChunkData: ((chunks: ChunkData[]) => void) | null = null;
    onBlockUpdate: ((updates: {
        x: number;
        y: number;
        tileType: number;
        action: string;
        inertiaDir?: number;
        playerId?: string;
        playerX?: number;
        playerY?: number;
        playerState?: PlayerState;
        playerFacing?: number;
        playerIsCrouching?: boolean;
        playerIsHoldingBlock?: boolean;
        playerHeldBlockType?: number;
    }[]) => void) | null = null;
    onBlockActionRollback: ((data: { action: string; x: number; y: number; blockType?: number; reason: string }) => void) | null = null;
    
    // Position-based callbacks
    onPlayerPositions: ((snapshots: PlayerSnapshot[]) => void) | null = null;
    onPlayerJoin: ((player: PlayerSnapshot) => void) | null = null;
    onPlayerLeave: ((playerId: string) => void) | null = null;
    onInitialState: ((localId: string, seed: number, players: PlayerSnapshot[]) => void) | null = null;
    
    // Air strafe callback (immediate horizontal movement)
    onAirStrafe: ((playerId: string, dir: number) => void) | null = null;

    // Combat callbacks
    onSlash: ((slash: SlashData) => void) | null = null;
    onDamage: ((playerId: string, hp: number, source: 'sword' | 'slash' | 'mouse' | 'drowning') => void) | null = null;
    onMaxHpUpdate: ((maxHp: number, hp: number) => void) | null = null;
    onDeath: ((playerId: string) => void) | null = null;
    onRuneUpdate: ((x: number, y: number, hieroglyphIndex: number) => void) | null = null;

    // Portal callbacks
    onTeleport: ((destination: PortalAddress, portalCode: number[]) => void) | null = null;

    // World reset callback (server world was reset)
    onWorldReset: ((seed: number) => void) | null = null;

    // Speech callback (player sent a message)
    onSpeech: ((playerId: string, words: number[], voice: string) => void) | null = null;

    // Mouse/rodent enemy callbacks
    onMouseSpawn: ((id: number, x: number, y: number, facing: number, rodentType: string, wallDir?: number, vertDir?: number) => void) | null = null;
    onMouseDeath: ((id: number) => void) | null = null;
    onMouseDespawn: ((id: number) => void) | null = null;

    // Block spawn callback (speech command put a block in player's hand)
    onBlockSpawn: ((blockType: TileType) => void) | null = null;

    // Jump height update callback (speech command changed jump height)
    onJumpHeightUpdate: ((jumpHeight: number) => void) | null = null;

    // Yellow key pickup callback
    onYellowKeyPickup: (() => void) | null = null;

    // Air update callback (server-authoritative breathing state)
    onAirUpdate: ((airTicks: number) => void) | null = null;

    // Pressure plate state change
    onPressurePlateChanged: ((x: number, y: number, active: boolean) => void) | null = null;

    // Sign glyph update
    onSignUpdate: ((x: number, y: number, glyphIndex: number) => void) | null = null;

    // Eye switch activation
    onEyeSwitchActivated: ((x: number, y: number, tick: number) => void) | null = null;

    // Spawn point set confirmation
    onSpawnPointSet: ((x: number, y: number) => void) | null = null;

    // Custom respawn (KIWEN_MI)
    onRespawnAt: ((x: number, y: number) => void) | null = null;

    // Server load indicator (0=healthy, 1=degraded, 2=critical)
    onServerLoad: ((level: number) => void) | null = null;

    constructor(serverUrl: string) {
        this.serverUrl = serverUrl;
        this.client = new Client(serverUrl);
    }

    async connect(options?: { isHoldingBlock?: boolean; heldBlockType?: number; facing?: number; deviceId?: string }): Promise<void> {
        this.onConnectionChange?.('connecting');

        try {
            this.room = await this.client.joinOrCreate('game', options);
            this.setupRoomListeners();
            this.onConnectionChange?.('connected');
        } catch (error) {
            console.error('Failed to connect:', error);
            this.onConnectionChange?.('error');
            throw error;
        }
    }

    private setupRoomListeners(): void {
        if (!this.room) return;

        // Receive initial state
        this.room.onMessage('init', (data: { 
            localId: string; 
            seed: number; 
            players: PlayerSnapshot[];
        }) => {
            this.playerCount = data.players.length;
            this.onPlayerCountChange?.(this.playerCount);
            this.onInitialState?.(data.localId, data.seed, data.players);
        });

        // Receive batched position updates for all other players
        this.room.onMessage('positions', (snapshots: PlayerSnapshot[]) => {
            this.onPlayerPositions?.(snapshots);
        });

        // Player joined
        this.room.onMessage('playerJoin', (player: PlayerSnapshot) => {
            this.playerCount++;
            this.onPlayerCountChange?.(this.playerCount);
            this.onPlayerJoin?.(player);
        });

        // Player left
        this.room.onMessage('playerLeave', (playerId: string) => {
            this.playerCount--;
            this.onPlayerCountChange?.(this.playerCount);
            this.onPlayerLeave?.(playerId);
        });

        // Chunk data
        this.room.onMessage('chunkData', (chunks: ChunkData[]) => {
            this.onChunkData?.(chunks);
        });

        // Block updates
        this.room.onMessage('blockUpdate', (updates: { x: number; y: number; tileType: number; action: string }[]) => {
            this.onBlockUpdate?.(updates);
        });

        // Block action rollback (race condition resolution)
        this.room.onMessage('blockActionRollback', (data: { action: string; x: number; y: number; blockType?: number; reason: string }) => {
            this.onBlockActionRollback?.(data);
        });

        // Air strafe from other player (immediate horizontal movement)
        this.room.onMessage('airStrafe', (data: { playerId: string; dir: number }) => {
            this.onAirStrafe?.(data.playerId, data.dir);
        });

        // Combat: Slash spawned
        this.room.onMessage('slash', (slash: SlashData) => {
            this.onSlash?.(slash);
        });

        // Combat: Player took damage
        this.room.onMessage('damage', (data: { playerId: string; hp: number; source: 'sword' | 'slash' | 'mouse' | 'drowning' }) => {
            this.onDamage?.(data.playerId, data.hp, data.source);
        });

        // Max HP increased (big heart container)
        this.room.onMessage('maxHpUpdate', (data: { maxHp: number; hp: number }) => {
            this.onMaxHpUpdate?.(data.maxHp, data.hp);
        });

        // Combat: Player died
        this.room.onMessage('death', (data: { playerId: string }) => {
            this.onDeath?.(data.playerId);
        });

        // Rune hieroglyph updated
        this.room.onMessage('runeUpdate', (data: { x: number; y: number; hieroglyphIndex: number }) => {
            this.onRuneUpdate?.(data.x, data.y, data.hieroglyphIndex);
        });

        // Portal teleport
        this.room.onMessage('teleport', (data: { destination: PortalAddress; portalCode: number[] }) => {
            this.onTeleport?.(data.destination, data.portalCode);
        });

        // World reset (server world was reset to fresh state)
        this.room.onMessage('worldReset', (data: { seed: number }) => {
            console.log(`[WORLD RESET] Server world reset with new seed: ${data.seed}`);
            this.onWorldReset?.(data.seed);
        });

        // Speech message from another player
        this.room.onMessage('speech', (data: { playerId: string; words: number[]; voice?: string }) => {
            this.onSpeech?.(data.playerId, data.words, data.voice || 'pona');
        });

        // Block spawn (speech command put a block in player's hand)
        this.room.onMessage('blockSpawn', (data: { blockType: TileType }) => {
            this.onBlockSpawn?.(data.blockType);
        });

        // Jump height update (speech command changed jump height)
        this.room.onMessage('jumpHeightUpdate', (data: { jumpHeight: number }) => {
            this.onJumpHeightUpdate?.(data.jumpHeight);
        });

        // Yellow key pickup
        this.room.onMessage('yellowKeyPickup', () => {
            this.onYellowKeyPickup?.();
        });

        // Air update (server-authoritative breathing state)
        this.room.onMessage('airUpdate', (data: { airTicks: number }) => {
            this.onAirUpdate?.(data.airTicks);
        });

        // Mouse/rodent enemy spawned
        this.room.onMessage('mouseSpawn', (data: { id: number; x: number; y: number; facing: number; rodentType?: RodentType; wallDir?: number; vertDir?: number }) => {
            this.onMouseSpawn?.(data.id, data.x, data.y, data.facing, data.rodentType || RodentType.MOUSE, data.wallDir, data.vertDir);
        });

        // Mouse enemy died
        this.room.onMessage('mouseDeath', (data: { id: number }) => {
            this.onMouseDeath?.(data.id);
        });

        // Mouse left subscribed chunks (client should remove sprite but mouse is still alive server-side)
        this.room.onMessage('mouseDespawn', (data: { id: number }) => {
            this.onMouseDespawn?.(data.id);
        });

        // Pressure plate state change
        this.room.onMessage('pressurePlateChanged', (data: { x: number; y: number; active: boolean }) => {
            this.onPressurePlateChanged?.(data.x, data.y, data.active);
        });

        // Sign glyph update
        this.room.onMessage('signUpdate', (data: { x: number; y: number; glyphIndex: number }) => {
            this.onSignUpdate?.(data.x, data.y, data.glyphIndex);
        });

        // Eye switch activated
        this.room.onMessage('eyeSwitchActivated', (data: { x: number; y: number; tick: number }) => {
            this.onEyeSwitchActivated?.(data.x, data.y, data.tick);
        });

        // Spawn point set confirmation
        this.room.onMessage('spawnPointSet', (data: { x: number; y: number }) => {
            this.onSpawnPointSet?.(data.x, data.y);
        });

        // Custom respawn at KIWEN_MI
        this.room.onMessage('respawnAt', (data: { x: number; y: number }) => {
            this.onRespawnAt?.(data.x, data.y);
        });

        // Server load level indicator
        this.room.onMessage('serverLoad', (data: { level: number }) => {
            this.onServerLoad?.(data.level);
        });

        // Handle disconnect
        this.room.onLeave((code) => {
            console.log('Disconnected with code:', code);
            this.onConnectionChange?.('disconnected');
        });

        this.room.onError((code, message) => {
            console.error('Room error:', code, message);
            this.onConnectionChange?.('error');
        });
    }

    // Send local player's authoritative position
    sendPosition(
        x: number,
        y: number,
        state: PlayerState,
        facing: number,
        isCrouching: boolean,
        isHoldingBlock: boolean,
        heldBlockType: TileType | null
    ): void {
        if (!this.room) return;

        this.positionSequence++;
        
        const update: PositionUpdate = {
            x,
            y,
            state,
            facing,
            isCrouching,
            isHoldingBlock,
            heldBlockType: heldBlockType ?? -1,
            timestamp: Date.now(),
            sequence: this.positionSequence,
        };

        this.room.send('position', update);
    }

    // Send block action (pickup/place/combine/swap)
    sendBlockAction(
        action: 'pickup' | 'place' | 'combine' | 'swap',
        x: number, 
        y: number, 
        blockType: TileType,
        playerX: number,
        playerY: number,
        playerState: PlayerState,
        playerFacing: number,
        playerIsCrouching: boolean,
        playerIsHoldingBlock: boolean,
        playerHeldBlockType: number
    ): void {
        if (!this.room) return;
        this.room.send('blockAction', { 
            action, 
            x, 
            y, 
            blockType,
            playerX,
            playerY,
            playerState,
            playerFacing,
            playerIsCrouching,
            playerIsHoldingBlock,
            playerHeldBlockType,
        });
    }

    subscribeToChunks(chunks: { cx: number; cy: number }[]): void {
        if (!this.room) return;
        this.room.send('subscribeChunks', chunks);
    }
    
    // Send air strafe (immediate horizontal movement while airborne)
    sendAirStrafe(dir: number): void {
        if (!this.room) return;
        this.room.send('airStrafe', { dir });
    }

    // Send sword hold state
    sendSwordHold(holding: boolean): void {
        if (!this.room) return;
        this.room.send('swordHold', { holding });
    }

    sendToolAction(action: string, playerX: number, playerY: number, facing: number, isCrouching: boolean): void {
        if (!this.room) return;
        this.room.send('toolAction', { action, playerX, playerY, facing, isCrouching });
    }

    // Request world reset (deletes save, creates fresh world with new seed)
    sendResetWorld(): void {
        if (!this.room) return;
        console.log('[WORLD RESET] Requesting server world reset...');
        this.room.send('resetWorld', {});
    }

    // Request portal activation (UP key while standing on rune)
    sendPortalActivate(): void {
        if (!this.room) return;
        this.room.send('portalActivate', {});
    }

    // Send speech message (toki pona glyph indices + voice profile key)
    sendSpeech(words: number[], voice: string = 'pona'): void {
        if (!this.room) return;
        this.room.send('speech', { words, voice });
    }

    // Cycle sign glyph (KIWEN_NIMI interaction)
    sendSignCycle(x: number, y: number): void {
        if (!this.room) return;
        this.room.send('signCycle', { x, y });
    }

    // Set spawn point (KIWEN_MI interaction)
    sendSetSpawnPoint(x: number, y: number): void {
        if (!this.room) return;
        this.room.send('setSpawnPoint', { x, y });
    }

    getLocalPlayerId(): string | null {
        return this.room?.sessionId ?? null;
    }

    async disconnect(): Promise<void> {
        const room = this.room;
        this.room = null;

        // Null all callbacks immediately so that stale messages from the old
        // room can't propagate through to GameClient after a new connection
        // is established. The room's onMessage handlers capture `this` via
        // closure, so nulling these properties makes them no-ops.
        this.onConnectionChange = null;
        this.onPlayerCountChange = null;
        this.onChunkData = null;
        this.onBlockUpdate = null;
        this.onBlockActionRollback = null;
        this.onPlayerPositions = null;
        this.onPlayerJoin = null;
        this.onPlayerLeave = null;
        this.onInitialState = null;
        this.onAirStrafe = null;
        this.onSlash = null;
        this.onDamage = null;
        this.onMaxHpUpdate = null;
        this.onDeath = null;
        this.onRuneUpdate = null;
        this.onTeleport = null;
        this.onWorldReset = null;
        this.onSpeech = null;
        this.onMouseSpawn = null;
        this.onMouseDeath = null;
        this.onMouseDespawn = null;
        this.onBlockSpawn = null;
        this.onJumpHeightUpdate = null;
        this.onAirUpdate = null;
        this.onPressurePlateChanged = null;
        this.onSignUpdate = null;
        this.onEyeSwitchActivated = null;
        this.onSpawnPointSet = null;
        this.onRespawnAt = null;

        if (!room) return;

        // Race room.leave() against a 1-second timeout. On degraded WebSockets
        // (e.g. after the browser tab was in the background), the close frame
        // may never send and room.leave() would hang indefinitely. The timeout
        // ensures we don't block the new connection. The server will eventually
        // clean up the stale session via its own inactivity timeout.
        try {
            await Promise.race([
                room.leave(),
                new Promise<void>((resolve) => setTimeout(resolve, 1000)),
            ]);
        } catch (e) {
            console.warn('[NETWORK] room.leave() error:', e);
        }
    }
}
